import { ExternalLink } from "lucide-react";
import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "~/atoms/alert";

const REDIRECT_URI = encodeURIComponent(
  `${window.location.origin}/api/v2/sp/admin-consent`,
);
const constructAdminConsentUrl = (clientId: string) =>
  `https://login.microsoftonline.com/organizations/adminconsent?client_id=${clientId}&redirect_uri=${REDIRECT_URI}`;

interface Props {
  clientId: string;
}

export const AdminConsentStep = ({ clientId }: Props) => {
  const { t } = useTranslation();

  const handleAdminConsent = () => {
    window.location.href = constructAdminConsentUrl(clientId);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <span>{t(($) => $.sharepoint.steps.adminConsent.header)}</span>
        </CardTitle>
        <CardDescription>
          {t(($) => $.sharepoint.steps.adminConsent.subtitle)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-6">
          <h3>
            {t(($) => $.sharepoint.steps.adminConsent.requiredPermissions)}
          </h3>
          <div className="grid gap-2 pt-2">
            <div className="grid gap-1">
              <h4 className="font-semibold">
                {t(($) => $.sharepoint.steps.adminConsent.application)}
              </h4>
              <p className="text-muted-foreground text-sm">
                {t(($) => $.sharepoint.steps.adminConsent.applicationDesc)}
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <div className="font-medium">
                {t(($) => $.sharepoint.steps.adminConsent.sitesSelected)}
              </div>
              <div className="text-muted-foreground text-sm">
                {t(($) => $.sharepoint.steps.adminConsent.sitesSelectedDesc)}
              </div>
            </div>
          </div>
          <div className="grid gap-2">
            <div className="grid gap-1">
              <h4 className="font-semibold">
                {t(($) => $.sharepoint.steps.adminConsent.delegated)}
              </h4>
              <p className="text-muted-foreground text-sm">
                {t(($) => $.sharepoint.steps.adminConsent.delegatedDesc)}
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <div className="font-medium">
                {t(($) => $.sharepoint.steps.adminConsent.userRead)}
              </div>
              <div className="text-muted-foreground text-sm">
                {t(($) => $.sharepoint.steps.adminConsent.userReadDesc)}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold">
            {t(($) => $.sharepoint.steps.adminConsent.whatHappensNext)}
          </h3>
          <ul className="text-muted-foreground space-y-2 text-sm">
            <li className="flex items-center space-x-2">
              <div className="bg-info size-1.5 rounded-full"></div>
              <span>
                {t(($) => $.sharepoint.steps.adminConsent.redirectToConsent)}
              </span>
            </li>
            <li className="flex items-center space-x-2">
              <div className="bg-info size-1.5 rounded-full"></div>
              <span>
                {t(($) => $.sharepoint.steps.adminConsent.reviewPermissions)}
              </span>
            </li>
            <li className="flex items-center space-x-2">
              <div className="bg-info size-1.5 rounded-full"></div>
              <span>
                {t(($) => $.sharepoint.steps.adminConsent.returnToContinue)}
              </span>
            </li>
          </ul>
        </div>

        <Alert variant="info">
          <AlertDescription>
            {t(($) => $.sharepoint.steps.adminConsent.adminRequiredDesc)}
          </AlertDescription>
        </Alert>
      </CardContent>
      <CardFooter>
        <Button onClick={handleAdminConsent}>
          <ExternalLink />
          {t(($) => $.sharepoint.steps.adminConsent.grantConsent)}
        </Button>
      </CardFooter>
    </Card>
  );
};
